import React, { useEffect, useState, useMemo } from 'react';
import SubSection from './SubSection';
import {lang} from '../../../common/baseData'
import langData from './lang.js'

// высоты элементов
const ELEMENT_HEIGHTS = {
  FIRST_ITEM: 18,    // первый элемент в колонке
  SECTION_SINGLE: 34,        // обычный раздел (1 строка)
  SECTION_DOUBLE: 52,        // раздел в 2 строки
  SUBSECTION_SINGLE: 24,     // подраздел (1 строка)
  SUBSECTION_DOUBLE: 42,     // подраздел в 2 строки
  VIEW_ALL: 24        // "Смотреть все"
};

const SUBSECTIONS_LIMIT = 15;
const MULTILINE_THRESHOLD = 25; // порог для определения 2 строк

function SubSections(props) {
  const {
    subSections,
    allSections,
    popularBlockUsed,
    sectionTreeHeight,
  } = props;

  const columnsLimit = popularBlockUsed ? 4 : 5;
  const [subSectionsChunks, setSubSectionsChunks] = useState([]);

  // создаем индекс для быстрого поиска подразделов
  const subsectionsByParentId = useMemo(() => {
    if (!Array.isArray(allSections)) return new Map();

    const index = new Map();

    allSections.forEach(section => {
      if (section.parentId != null) {
        const parentId = Number(section.parentId);
        if (!index.has(parentId)) {
          index.set(parentId, []);
        }
        index.get(parentId).push(section);
      }
    });

    return index;
  }, [allSections]);

  // оптимизированная функция получения подразделов
  const getSubSections = (section) => {
    const sectionId = Number(section.itemId);
    return subsectionsByParentId.get(sectionId) || [];
  };

  // функция определения высоты элемента
  const getItemHeight = (text, isSection = true) => {
    // если текст пустой или очень короткий
    if (!text) {
      return isSection ? ELEMENT_HEIGHTS.SECTION_SINGLE : ELEMENT_HEIGHTS.SUBSECTION_SINGLE;
    }

    // если текст длиннее 25 символов - считаем как 2 строки
    if (text.length > MULTILINE_THRESHOLD) {
      return isSection ? ELEMENT_HEIGHTS.SECTION_DOUBLE : ELEMENT_HEIGHTS.SUBSECTION_DOUBLE;
    }

    // иначе - одна строка
    return isSection ? ELEMENT_HEIGHTS.SECTION_SINGLE : ELEMENT_HEIGHTS.SUBSECTION_SINGLE;
  };

  // функция создания ссылки "Смотреть все"
  const createViewAllSection = (section) => ({
    title: getMessage('VIEW_ALL') || '',
    link: section.link,
    virtualTitle: true,
    newSection: false,
    viewAll: true
  });

  // расчёт чанков подразделов
  useEffect(() => {
    if (!Array.isArray(subSections) || !subSections.length || !allSections.length) {
      setSubSectionsChunks([]);
      return;
    }

    const chunks = [];
    let currentChunk = [];
    let currentHeight = 0;

    // основной цикл по разделам
    outerLoop: for (const section of subSections) {
      const subsections = getSubSections(section);

      // рассчитываем высоту главного раздела
      const sectionHeight = getItemHeight(section.title || section.name, true);

      // рассчитываем полную высоту раздела
      let subsectionsHeight = 0;
      const limitedSubsections = subsections.slice(0, SUBSECTIONS_LIMIT);

      for (const sub of limitedSubsections) {
        subsectionsHeight += getItemHeight(sub.title || sub.name, false);
      }

      const hasViewAll = subsections.length > SUBSECTIONS_LIMIT;
      const viewAllHeight = hasViewAll ? ELEMENT_HEIGHTS.VIEW_ALL : 0;

      const sectionFullHeight = sectionHeight + subsectionsHeight + viewAllHeight;

      // проверяем, поместится ли раздел целиком
      if (currentHeight + sectionFullHeight > sectionTreeHeight && currentChunk.length > 0) {
        chunks.push(currentChunk);
        if (chunks.length >= columnsLimit) break;
        currentChunk = [];
        currentHeight = 0;
      }

      // добавляем главный раздел
      const currentSectionHeight = currentChunk.length === 0
        ? ELEMENT_HEIGHTS.FIRST_ITEM
        : sectionHeight;

      currentChunk.push(section);
      currentHeight += currentSectionHeight;

      // добавляем подразделы
      let addedSubsections = 0;

      for (const subsection of subsections) {
        if (addedSubsections >= SUBSECTIONS_LIMIT) break;

        const subsectionHeight = getItemHeight(subsection.title || subsection.name, false);

        // проверяем, помещается ли подраздел
        if (currentHeight + subsectionHeight > sectionTreeHeight) {
          chunks.push(currentChunk);
          if (chunks.length >= columnsLimit) break outerLoop;
          currentChunk = [];
          currentHeight = 0;
        }

        currentChunk.push(subsection);
        currentHeight += subsectionHeight;
        addedSubsections++;
      }

      // добавляем "Смотреть все", если есть ещё подразделы
      if (hasViewAll) {
        if (currentHeight + ELEMENT_HEIGHTS.VIEW_ALL > sectionTreeHeight) {
          chunks.push(currentChunk);
          if (chunks.length >= columnsLimit) break;
          currentChunk = [];
          currentHeight = 0;
        }

        currentChunk.push(createViewAllSection(section));
        currentHeight += ELEMENT_HEIGHTS.VIEW_ALL;
      }

      // проверяем лимит колонок после обработки каждого раздела
      if (chunks.length >= columnsLimit) break;
    }

    // сохраняем последний чанк, если он не пустой
    if (currentChunk.length > 0 && chunks.length < columnsLimit) {
      chunks.push(currentChunk);
    }

    setSubSectionsChunks(chunks);
  }, [sectionTreeHeight, subSections, allSections, columnsLimit, subsectionsByParentId]);

  // определяем CSS класс для ширины колонок
  const columnWidthClass = subSectionsChunks.length >= 3 ? 'basis-1/4' : 'basis-1/3';

  /**
   * Загрузка языковых сообщений
   * @param message
   * @return {string}
   */
  function getMessage (message) {
    return langData[lang][message] || ''
  }

  return (
    <>
      {
        subSectionsChunks.map((chunk, chunkIndex) => (
          <ul className={`flex flex-col ${columnWidthClass}`}
            key={`submenu-column-${chunkIndex}`}>
            {
              chunk.map((item, index) => (
                <SubSection
                  key={`${chunkIndex}-column-${index}`}
                  first={index === 0}
                  {...item}/>
              ))
            }
          </ul>
        ))
      }
    </>
  );
}

export default SubSections;